import React, { useContext, useEffect, useState } from 'react';
import { useRecoilState, useResetRecoilState } from 'recoil';

import {
  Box,
  Button,
  Divider,
  Flex,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
} from '@chakra-ui/react';

import { UserIcon } from '@asa/asasqt-microfrontend-components/dist/Icons/User';
import { GoChevronRight } from '@asa/asasqt-microfrontend-components/dist/Icons/lib/GoChevronRight';
import { RiArrowDownSFill } from '@asa/asasqt-microfrontend-components/dist/Icons/lib/RiArrowDownSFill';
import { RiErrorWarningFill } from '@asa/asasqt-microfrontend-components/dist/Icons/lib/RiErrorWarningFill';
import {
  PORTAL_ROUTES,
  PORTAL_ROUTES_NAMES,
} from '@asa/asasqt-microfrontend-components/dist/common/api/myAsaSqt/portal';
import { userDetailsAtom } from '@asa/asasqt-microfrontend-components/dist/common/state/shared/auth';
import { SqtEventAction } from '@asa/asasqt-microfrontend-components/dist/common/types/shared/matomo';
import { Icon } from '@asa/asasqt-microfrontend-components/dist/typography/Icons';
import { Text } from '@asa/asasqt-microfrontend-components/dist/typography/Text';

import { setRememberMeFlagAtom } from '@/core/auth/core/auth.recoil';
import { LanguageContext } from '@/core/common/language';
import {
  RedirectLandingPages,
  myAsaInfoContactPage,
  myAsaInfoDownloadsPage,
  myAsaInfoLandingPage,
} from '@/core/settings/utils';

import { useAxiosIntercept } from '../../axios/axios.context';
import useLogout from '../../hooks/useLogout';
import { legalDocumentsAtom } from '../../recoil';
import { Documents, LegalDocType } from '../../types';
import { MenuLink } from './menuLink';

export const AccountMenu = ({ ...rest }) => {
  const { tr } = useContext(LanguageContext);
  const { initiateLogout } = useLogout();
  const axiosIntercept = useAxiosIntercept();

  const [userDetails] = useRecoilState(userDetailsAtom);
  const [legalDocs] = useRecoilState<Documents>(legalDocumentsAtom);
  const resetRememberMeFlag = useResetRecoilState(setRememberMeFlagAtom);

  const [asaApiStageCookie, setAsaApiStageCookie] = useState<string | null>(
    null
  );

  // Function to get cookie value by name
  const getCookieValue = (name: string): string | null => {
    const match = document.cookie.match(
      new RegExp('(^| )' + name + '=([^;]+)')
    );
    return match ? match[2] : null;
  };

  // Check for the cookie on component mount and when document.cookie changes
  useEffect(() => {
    const checkCookie = () => {
      const cookieValue = getCookieValue('lx-upds-asaapissqt');
      setAsaApiStageCookie(cookieValue);
    };

    checkCookie();

    // Set up an interval to check for cookie changes
    const intervalId = setInterval(checkCookie, 60 * 1000);

    return () => clearInterval(intervalId);
  }, []);

  const clearAsaApiStageCookie = async () => {
    try {
      await axiosIntercept.delete(
        PORTAL_ROUTES[PORTAL_ROUTES_NAMES.TEST_COOKIES]
      );
      setAsaApiStageCookie(null);
      window.location.reload();
    } catch (error) {
      console.error('Failed to clear ASA API stage cookie:', error);
    }
  };

  const triggerLogout = () => {
    initiateLogout();

    resetRememberMeFlag();
  };

  return (
    <Menu placement="bottom-end" {...rest}>
      <Divider orientation="vertical" />
      <MenuButton
        data-testid="accountmenu-button"
        color={'gray.900'}
        borderRadius="full"
      >
        <Box
          display={'inline-flex'}
          alignContent={'center'}
          pl={4}
          position={'relative'}
          top={2}
        >
          <Icon icon={UserIcon} fontSize={24} right={'2px'} />
          {!!asaApiStageCookie && (
            <Icon
              icon={RiErrorWarningFill}
              color="orange.400"
              fontSize={20}
              position="absolute"
              left="32px"
              top="-11px"
            />
          )}
          <Text ml={2} display={{ base: 'none', lg: 'inline-block' }}>
            {userDetails && (
              <>
                {userDetails.accountLogin}\{userDetails.userLogin}
              </>
            )}
          </Text>
          <Icon
            fontSize={16}
            icon={RiArrowDownSFill}
            position={'relative'}
            top={1}
          />
        </Box>
      </MenuButton>
      <MenuList
        maxW="12rem"
        overflow="hidden"
        color="gray.800"
        _dark={{ color: 'white' }}
      >
        {!!userDetails && (
          <>
            <Flex
              mx={3}
              flexDirection={'column'}
              display={{ base: 'flex', lg: 'none' }}
            >
              <Text fontSize={'sm'} fontWeight={'bold'}>
                {userDetails.userLogin}
              </Text>
              <Text fontSize={'sm'}>{userDetails.accountLogin}</Text>
            </Flex>
            <MenuDivider display={{ base: 'block', lg: 'none' }} />
          </>
        )}

        <MenuLink
          dataTestId={'myasainfo-button'}
          redirectPage={RedirectLandingPages.MyAsaInfoPortal}
          landingPage={myAsaInfoLandingPage}
          translation={'common.label.myASAInfo'}
          matomoAction={SqtEventAction.ALL_PAGES_GO_TO_MYASAINFO}
        />
        <Menu>
          <MenuButton
            data-testid="legal_information-button"
            as={Button}
            pt={1.5}
            pb={1.5}
            paddingInline={3}
            background={'white'}
            borderRadius={0}
            fontWeight={'normal'}
            _hover={{ bg: 'gray.100' }}
            w={'100%'}
            textAlign={'start'}
            rightIcon={<GoChevronRight />}
          >
            {tr('common.label.legal.information')}
          </MenuButton>
          <MenuList>
            <MenuItem
              data-testid="privacy-button"
              onClick={() =>
                window.open(legalDocs[LegalDocType.AsaPrivacy]?.url, '_blank')
              }
            >
              {tr('common.label.legal.privacy')}
            </MenuItem>
            <MenuItem
              data-testid="imprint-button"
              onClick={() =>
                window.open(
                  legalDocs[LegalDocType.AsaLegalNotice]?.url,
                  '_blank'
                )
              }
            >
              {tr('common.label.legal.imprint')}
            </MenuItem>
            <MenuItem
              data-testid="dataProcessing-button"
              onClick={() =>
                window.open(
                  legalDocs[LegalDocType.AsaSqtDataProcessing]?.url,
                  '_blank'
                )
              }
            >
              {tr('common.label.legal.dataProcessing.title')}
            </MenuItem>
            <MenuItem
              data-testid="tos-button"
              onClick={() =>
                window.open(legalDocs[LegalDocType.AsaTos]?.url, '_blank')
              }
            >
              {tr('common.label.legal.tos.title')}
            </MenuItem>
          </MenuList>
        </Menu>
        <MenuLink
          dataTestId={'feedback-button'}
          redirectPage={RedirectLandingPages.MyAsaInfoPortal}
          landingPage={myAsaInfoContactPage}
          translation={'common.label.feedback'}
          matomoAction={SqtEventAction.ALL_PAGES_FEEDBACK}
        />
        <MenuLink
          dataTestId={'help-button'}
          redirectPage={RedirectLandingPages.MyAsaInfoPortal}
          landingPage={myAsaInfoDownloadsPage}
          translation={'common.label.help'}
          matomoAction={SqtEventAction.ALL_PAGES_HELP}
        />

        <MenuDivider />
        <MenuItem data-testid="logout-button" onClick={() => triggerLogout()}>
          {tr('common.label.action.logout')}
        </MenuItem>
        {!!asaApiStageCookie && (
          <>
            <MenuDivider />
            <Flex
              px={3}
              flexDirection={'column'}
              borderLeft={'2px'}
              borderLeftColor={'orange.400'}
            >
              <Text fontSize={'sm'}>Inactive stage of ASA APIs is in use</Text>
              <Flex mt={1} alignItems="center" justifyContent="space-between">
                <Text fontSize="xs" maxWidth="120px" color="gray.500">
                  {asaApiStageCookie}
                </Text>
                <Button
                  size="xs"
                  colorScheme="orange"
                  onClick={clearAsaApiStageCookie}
                  data-testid="clear-asa-api-stage-cookie"
                >
                  Clear
                </Button>
              </Flex>
            </Flex>
          </>
        )}
      </MenuList>
    </Menu>
  );
};
